import { useCallback, useEffect, useState } from 'react'
import { getVisionSessions, putVisionSession } from '../visionApi'

// C16.7: the list is asked again every 15 s, like the summary.
const SESSIONS_REFRESH_MS = 15000

/**
 * Story 213, reopening 4 (L2, C16.4, C16.7) — the open sessions of MIC for the block "Sessions" of Camera control. Asks Sun
 * only while `enabled` (the vision is on, RN146). Watching or not is an action applied at once: the answer of Sun is the
 * whole list, painted as it comes; a rejection goes to `onError` and leaves the list as it was (S344).
 */
const useVisionSessions = (enabled, onError) => {
  const [sessions, setSessions] = useState([])

  useEffect(() => {
    if (!enabled) {
      setSessions([])
      return
    }
    let cancelled = false
    const refresh = () => getVisionSessions()
      .then((answer) => {
        if (!cancelled) {
          setSessions(Array.isArray(answer) ? answer : [])
        }
      })
      .catch(() => {})
    refresh()
    const timer = setInterval(refresh, SESSIONS_REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [enabled])

  const choose = useCallback(async (id, body) => {
    try {
      const answer = await putVisionSession(id, body)
      if (Array.isArray(answer)) {
        setSessions(answer)
      }
    } catch (error) {
      onError((error && error.message) || '¡Ups, something has gone wrong!')
    }
  }, [onError])

  // RN143: with `instead`, one single change (that session stops being watched and this one takes its slot).
  const watch = useCallback((id, instead = '') => choose(id, instead === '' ? { watched: true } : { watched: true, instead }), [choose])
  const stopWatching = useCallback((id) => choose(id, { watched: false }), [choose])

  return { sessions, watch, stopWatching }
}

export default useVisionSessions
